"use client"

import React, { useState } from "react"

import { ResolvedApplicationTreeItem } from "@/lib/kontent/config/getApplicationTree"
import { getSyncDictionary } from "@/lib/sync-dictionary"
import { cn, resolveButtonHref, transformButtonStyle } from "@/lib/utils"
import CaNextBankLogo from "@/components/icons/CaNextBankLogo"
import { BurgerMenu, Link, RemixIcon } from "@/components/ui/atoms"
import { MainNavigationMenu, TNavItem } from "@/components/ui/organisms"

import { SearchBar } from "../../molecules/search-bar/SearchBar"

type TNavButtons = {
  id?: string
  codename?: string
  ctaLabel: string
  ctaLink: string
  linkedPage?: ResolvedApplicationTreeItem
  style: string
  iconRight?: string | null
  iconLeft?: string | null
}

type TNavigationProps = {
  className?: string
  toggleMenu: () => void
  isMenuOpen: boolean
  navButtons: TNavButtons[]
  navItems: TNavItem[]
  onSearchClick?: () => void
}

const Navigation = ({
  className,
  toggleMenu,
  isMenuOpen,
  navButtons,
  navItems,
}: TNavigationProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [input, setInput] = useState("")

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen)
    setInput("")
  }

  return (
    <section
      className={cn(
        "navigation",
        isMenuOpen ? "navigation--is-open" : "",
        className
      )}
    >
      <div className="navigation__inner justify-between">
        <section className="navigation__heading">
          <Link
            chapters={["Navigation", getSyncDictionary().page.home.name]}
            href="/"
            variant="ghost"
            className="p-0 no-underline"
            title={getSyncDictionary().page.home.name}
          >
            <CaNextBankLogo
              className={cn(
                "h-[65px] xl:h-[85px]",
                isSearchOpen ? "hidden xl:block" : ""
              )}
            />
          </Link>

          <>
            <SearchBar
              searchlabel={"Rechercher"}
              isOpen={isSearchOpen}
              toggleSearch={toggleSearch}
              className="flex xl:hidden"
              setIsSearchOpen={setIsSearchOpen}
              inputValue={input}
            />
            <RemixIcon
              size="lg"
              name={"Close"}
              className={cn(isSearchOpen ? "ml-6 xl:hidden" : "hidden")}
              onClick={toggleSearch}
            />
          </>
          <div
            className={cn(
              "flex items-center gap-4 xs:gap-6 xl:hidden",
              isSearchOpen ? "hidden" : ""
            )}
          >
            <BurgerMenu
              onClick={toggleMenu}
              isOpen={isMenuOpen}
              aria-controls="main-navigation"
            />
          </div>
        </section>
        <SearchBar
          searchlabel={"Rechercher"}
          isOpen={true}
          toggleSearch={toggleSearch}
          className="hidden xl:flex xl:grow xl:justify-end"
          setIsSearchOpen={setIsSearchOpen}
          inputValue={input}
        />
        <section id="main-navigation" className="navigation__content">
          <span className="xl:hidden">
            {navItems ? <MainNavigationMenu navItems={navItems} /> : null}
          </span>
          <div className="navigation__actions xl:order-3">
            <div className="w-full bg-neutral-200 p-2 xl:w-fit xl:bg-neutral-100 xl:p-0">
              <ul className="mx-auto flex items-center gap-9 xl:gap-5 2xl:gap-6">
                {navButtons
                  ? navButtons.map((button) => {
                      return (
                        <li key={button.codename} className="hidden xl:block">
                          <Link
                            chapters={["Navigation", button.ctaLabel]}
                            variant={transformButtonStyle(button.style)}
                            iconLeft={button.iconLeft ?? undefined}
                            iconRight={button.iconRight ?? undefined}
                            href={resolveButtonHref(button)}
                            id={button.codename}
                          >
                            {button.ctaLabel}
                          </Link>
                        </li>
                      )
                    })
                  : null}
              </ul>
            </div>
            <div className="w-full bg-neutral-200 px-6 pb-4 xl:hidden">
              {navButtons
                ? navButtons.map((button) => {
                    return (
                      <span key={button.codename}>
                        {/* Forcing ghost button as designed */}
                        <Link
                          chapters={["Navigation", button.ctaLabel]}
                          variant="ghost"
                          className="flex flex-col no-underline"
                          iconLeft={button.iconLeft ?? undefined}
                          iconRight={button.iconRight ?? undefined}
                          href={resolveButtonHref(button)}
                          id={button.codename}
                        >
                          {button.ctaLabel}
                        </Link>
                      </span>
                    )
                  })
                : null}
            </div>
          </div>
        </section>
      </div>
      <section className="navigation__content navigation__content--desktop hidden xl:flex">
        {navItems ? <MainNavigationMenu navItems={navItems} /> : null}
      </section>
      <div
        className={cn("navigation-overlay", isSearchOpen ? "" : "hidden")}
        role="button"
        tabIndex={0}
        aria-label="Fermer la recherche"
        onClick={toggleSearch}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            toggleSearch()
          }
        }}
      ></div>
    </section>
  )
}

export { Navigation, type TNavButtons, type TNavigationProps }
